import React, { useState, useEffect, useContext, useCallback } from "react";
import { UserContext, API } from "@/App";
import { TYPE_CONFIG, getRandomEncouragement, getRandomMilestoneMessage, GOLD_COIN_URL, TREASURE_ICONS } from "@/data/gameData";
import { Badge } from "@/components/ui/badge";
import PracticeMode from "@/pages/PracticeMode";
import { Heart, ChevronRight, ArrowLeft, Trash2, RotateCcw, Star, Check, AlertTriangle } from "lucide-react";
import { playCoinSound, playCelebrationSound } from "@/data/sounds";
import axios from "axios";
import { toast } from "sonner";

// ── Treasure Chest Unboxing Celebration ──
function CelebrationOverlay({ data, hasMilestone, onClose }) {
  const [phase, setPhase] = React.useState(hasMilestone ? "chest" : "coin"); // chest → reveal → done | coin → done

  const openChest = () => {
    playCelebrationSound();
    setPhase("reveal");
  };

  React.useEffect(() => {
    if (!hasMilestone) playCoinSound();
  }, [hasMilestone]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(74, 50, 36, 0.7)", backdropFilter: "blur(6px)" }}
      data-testid="celebration-overlay">
      <div className="open-book max-w-sm w-full p-8 text-center slide-up">

        {/* Phase: Treasure Chest (tap to open) */}
        {phase === "chest" && (
          <>
            <p className="font-heading text-lg font-bold mb-4" style={{ color: "#D97706" }}>
              You found a treasure chest!
            </p>
            <button onClick={openChest} className="relative mx-auto block mb-4 focus:outline-none"
              data-testid="tap-chest-btn">
              {/* Chest glow */}
              <div className="absolute inset-0 rounded-2xl pulse-glow" style={{ margin: -8 }} />
              {/* Chest SVG */}
              <svg width="140" height="120" viewBox="0 0 140 120" className="chest-shake mx-auto">
                {/* Chest bottom */}
                <rect x="15" y="55" width="110" height="55" rx="8" fill="#92400E" stroke="#5C3D2E" strokeWidth="3" />
                <rect x="20" y="60" width="100" height="45" rx="4" fill="#B45309" />
                {/* Wood slats */}
                <line x1="20" y1="75" x2="120" y2="75" stroke="#92400E" strokeWidth="1" />
                <line x1="20" y1="90" x2="120" y2="90" stroke="#92400E" strokeWidth="1" />
                {/* Chest lid */}
                <path d="M15 55 Q70 25 125 55" fill="#A0522D" stroke="#5C3D2E" strokeWidth="3" />
                <path d="M20 55 Q70 30 120 55" fill="#CD853F" />
                {/* Lock/clasp */}
                <rect x="60" y="48" width="20" height="16" rx="3" fill="#FFC107" stroke="#B8860B" strokeWidth="2" />
                <circle cx="70" cy="56" r="3" fill="#B8860B" />
                {/* Gleam */}
                <circle cx="45" cy="42" r="4" fill="#FFC107" opacity="0.6" />
                <circle cx="95" cy="42" r="3" fill="#FFC107" opacity="0.4" />
              </svg>
            </button>
            <p className="font-heading text-sm" style={{ color: "#7A5946" }}>
              Tap the chest to reveal your treasure!
            </p>
          </>
        )}

        {/* Phase: Reveal (chest opened) */}
        {phase === "reveal" && (
          <>
            {/* Open chest with treasure coming out */}
            <div className="relative mb-4">
              <svg width="140" height="100" viewBox="0 0 140 100" className="mx-auto">
                {/* Chest open bottom */}
                <rect x="15" y="45" width="110" height="50" rx="8" fill="#92400E" stroke="#5C3D2E" strokeWidth="3" />
                <rect x="20" y="50" width="100" height="40" rx="4" fill="#B45309" />
                {/* Open lid tilted back */}
                <path d="M15 45 L5 15 Q70 -10 135 15 L125 45" fill="#A0522D" stroke="#5C3D2E" strokeWidth="2" />
                <path d="M10 17 Q70 -5 130 17 L125 43 Q70 20 15 43 Z" fill="#CD853F" />
                {/* Gold glow from inside */}
                <ellipse cx="70" cy="48" rx="40" ry="15" fill="#FFC107" opacity="0.3" />
              </svg>
              {/* Treasure floating up */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 slide-up">
                {data.newRewards?.map((r, i) => {
                  const icon = TREASURE_ICONS[r.type] || "🎁";
                  return <span key={i} className="text-5xl block">{icon}</span>;
                })}
              </div>
              {/* Burst particles */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="absolute w-2.5 h-2.5 rounded-full burst-effect"
                    style={{
                      background: ["#FFC107","#FB7185","#38BDF8","#4ADE80","#A855F7","#F59E0B","#EC4899","#06B6D4","#FFC107","#DC2626","#22C55E","#8B5CF6"][i],
                      animationDelay: `${i * 0.04}s`,
                      transform: `rotate(${i * 30}deg) translateX(70px)`
                    }} />
                ))}
              </div>
            </div>

            <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: "#D97706" }}>
              Treasure Unlocked!
            </h2>
            {data.newRewards?.map((r, i) => {
              const icon = TREASURE_ICONS[r.type] || "🎁";
              return (
                <div key={i} className="tt-card p-4 mb-3 pulse-glow" style={{ background: "linear-gradient(135deg, #FEF3C7, #FDE68A)" }}>
                  <span className="text-4xl block mb-1">{icon}</span>
                  <p className="font-heading text-lg font-bold" style={{ color: "#92400E" }}>{r.type}</p>
                </div>
              );
            })}
            <p className="font-heading text-base font-semibold mb-1" style={{ color: "#4A3224" }}>
              +1 Gold Coin Earned!
            </p>
            <p className="text-sm mb-4" style={{ color: "#7A5946" }}>
              {getRandomMilestoneMessage()}
            </p>
            <p className="text-xs mb-4" style={{ color: "#7A5946" }}>
              Total: {data.goldCoins} coins | {data.totalMemorized} items memorized
            </p>
            <button onClick={onClose} className="tt-btn tt-btn-gold w-full py-3 text-lg"
              data-testid="celebration-continue-btn">Continue Adventure!</button>
          </>
        )}

        {/* Phase: Simple coin (no milestone) */}
        {phase === "coin" && (
          <>
            <div className="relative mb-4">
              <img src={GOLD_COIN_URL} alt="Gold Coin" className="w-24 h-24 mx-auto coin-flip object-contain" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="absolute w-3 h-3 rounded-full burst-effect"
                    style={{
                      background: ["#FFC107","#FB7185","#38BDF8","#4ADE80","#A855F7","#F59E0B","#EC4899","#06B6D4"][i],
                      animationDelay: `${i * 0.05}s`,
                      transform: `rotate(${i * 45}deg) translateX(60px)`
                    }} />
                ))}
              </div>
            </div>

            {/* Rank Promotion */}
            {data.rankPromoted && (
              <div className="tt-card p-4 mb-3 pulse-glow" style={{ background: "linear-gradient(135deg, #FEF3C7, #FDE68A)", border: "3px solid #D97706" }}>
                <span className="text-3xl block mb-1">⚓</span>
                <p className="font-heading text-lg font-bold" style={{ color: "#4A3224" }}>You've Been Promoted!</p>
                <p className="font-heading text-base font-bold" style={{ color: "#D97706" }}>{data.newRankName}</p>
              </div>
            )}

            <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: "#D97706" }}>
              {data.rankPromoted ? "Congratulations!" : "Congratulations!"}
            </h2>
            <p className="font-heading text-lg font-semibold mb-1" style={{ color: "#4A3224" }}>+1 Gold Coin Earned!</p>
            <p className="text-sm mb-4" style={{ color: "#7A5946" }}>{getRandomEncouragement()}</p>
            <p className="text-xs mb-4" style={{ color: "#7A5946" }}>
              Total: {data.goldCoins} coins | {data.totalMemorized} items memorized
            </p>
            <button onClick={onClose} className="tt-btn tt-btn-gold w-full py-3 text-lg"
              data-testid="celebration-continue-btn">Continue Adventure!</button>
          </>
        )}
      </div>
    </div>
  );
}

function TypeIcon({ type, size = 48 }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.other;
  if (config.iconImage) {
    return <img src={config.iconImage} alt={config.label} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-full flex items-center justify-center" style={{ width: size, height: size, background: config.bg, border: `2px solid ${config.color}` }}>
      <span style={{ fontSize: size * 0.4 }}>{config.emoji}</span>
    </div>
  );
}

export default function MyCollection({ refreshStats }) {
  const { currentUser } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [view, setView] = useState("choose");
  const [filterStatus, setFilterStatus] = useState("not_working_on");
  const [filterType, setFilterType] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [practiceMode, setPracticeMode] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [reviewItem, setReviewItem] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [celebrationData, setCelebrationData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(() => {
    if (!currentUser) return;
    axios.get(`${API}/users/${currentUser.id}/items`)
      .then(r => { setItems(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentUser]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const toggleFavorite = async (item) => {
    try {
      await axios.put(`${API}/users/${currentUser.id}/items/${item.id}`, { is_favorite: !item.is_favorite });
      fetchItems();
    } catch { toast.error("Failed to update"); }
  };

  const removeFromCollection = async (item) => {
    if (window.confirm(`Remove "${item.title}" from your collection?`)) {
      try {
        await axios.delete(`${API}/users/${currentUser.id}/items/${item.id}`);
        toast.success("Removed from collection");
        fetchItems();
        if (refreshStats) refreshStats();
        setView("choose");
      } catch { toast.error("Failed to remove"); }
    }
  };

  const changeStatus = async (item, newStatus) => {
    try {
      await axios.put(`${API}/users/${currentUser.id}/items/${item.id}`, { status: newStatus });
      const labels = { not_working_on: "Saved", working_on: "Working On", memorized: "Memorized" };
      toast.success(`Moved to ${labels[newStatus]}`);
      fetchItems();
      setSelectedItem(prev => prev ? { ...prev, status: newStatus } : prev);
    } catch { toast.error("Failed to update"); }
  };

  const fetchReviews = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const { data } = await axios.get(`${API}/users/${currentUser.id}/reviews`);
      setReviewData(data);
    } catch { setReviewData({ due: [], upcoming: [], total_due: 0, total_upcoming: 0 }); }
  }, [currentUser?.id]);

  useEffect(() => {
    if (filterStatus === "review") fetchReviews();
  }, [filterStatus, fetchReviews]);

  const submitReview = async (itemId, rating) => {
    try {
      const { data } = await axios.post(`${API}/users/${currentUser.id}/reviews/${itemId}`, { rating });
      setReviewResult(data);
      if (data.rank_promoted) {
        toast.success(`Rank up! You're now a ${data.new_rank_name}!`);
      } else if (data.tier4_bonus) {
        toast.success(`This one's truly yours now! +${data.coins_earned} coins`);
      } else if (data.coins_earned > 0) {
        toast.success(`+${data.coins_earned} coin! ${data.tier_name}`);
      } else {
        toast.success(data.tier_name);
      }
      fetchReviews();
      if (refreshStats) refreshStats();
    } catch { toast.error("Failed to submit review"); }
  };

  const markMemorized = async (item) => {
    try {
      const { data } = await axios.post(`${API}/users/${currentUser.id}/mark-memorized/${item.id}`);
      // Only celebrate if it's a NEW memorization (not already memorized)
      if (data.already_memorized) {
        toast.success("Great review! Keep practicing!");
        setView("choose");
        return;
      }
      if (data.new_rewards?.length > 0) {
        playCelebrationSound();
      } else {
        playCoinSound();
      }
      setCelebrationData({
        goldCoins: data.gold_coins,
        newRewards: data.new_rewards || [],
        totalMemorized: data.total_memorized,
        rank: data.rank,
        rankPromoted: data.rank_promoted,
        newRankName: data.new_rank_name
      });
      fetchItems();
      if (refreshStats) refreshStats();
    } catch { toast.error("Failed to mark"); }
  };

  const closeCelebration = () => {
    setCelebrationData(null);
    setView("choose");
  };

  const favorites = items.filter(i => i.is_favorite);
  const typeGroups = {};
  items.filter(i => i.status === filterStatus).forEach(i => {
    typeGroups[i.type] = typeGroups[i.type] || [];
    typeGroups[i.type].push(i);
  });
  const currentTypeItems = filterType ? items.filter(i => i.type === filterType && i.status === filterStatus) : [];

  // ═══════ CELEBRATION OVERLAY ═══════
  if (celebrationData) {
    const hasMilestone = celebrationData.newRewards?.length > 0;
    return <CelebrationOverlay
      data={celebrationData}
      hasMilestone={hasMilestone}
      onClose={closeCelebration}
    />;
  }

  // ═══════ CHOOSE VIEW ═══════
  if (view === "choose") {
    return (
      <div className="slide-up" data-testid="my-collection-page">
        <div className="open-book p-6 mb-4">
          <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: "#4A3224" }}>My Treasure Tome</h2>
          <p className="text-sm mb-4" style={{ color: "#7A5946" }}>Add items to your collection, memorize them to earn gold, and review regularly to deepen your memory and climb the ranks!</p>

          {/* Status Toggle - 5 sections */}
          <div className="flex gap-1 mb-5 flex-wrap">
            <button onClick={() => setFilterStatus("favorites")}
              className={`tt-btn px-2 py-2 text-xs flex-1 ${filterStatus === "favorites" ? "tt-btn-coral" : ""}`}
              style={filterStatus !== "favorites" ? { background: "white" } : {}}
              data-testid="filter-favorites">
              <Heart className="w-3 h-3 inline mr-0.5" fill={filterStatus === "favorites" ? "#fff" : "#D97706"} style={{ color: filterStatus === "favorites" ? "#fff" : "#D97706" }} /> Faves
            </button>
            <button onClick={() => setFilterStatus("not_working_on")}
              className={`tt-btn px-2 py-2 text-xs flex-1 ${filterStatus === "not_working_on" ? "tt-btn-ocean" : ""}`}
              style={filterStatus !== "not_working_on" ? { background: "white" } : {}}
              data-testid="filter-not-working-on">
              Saved
            </button>
            <button onClick={() => setFilterStatus("working_on")}
              className={`tt-btn px-2 py-2 text-xs flex-1 ${filterStatus === "working_on" ? "tt-btn-gold" : ""}`}
              style={filterStatus !== "working_on" ? { background: "white" } : {}}
              data-testid="filter-working-on">
              Working On
            </button>
            <button onClick={() => setFilterStatus("memorized")}
              className={`tt-btn px-2 py-2 text-xs flex-1 ${filterStatus === "memorized" ? "tt-btn-green" : ""}`}
              style={filterStatus !== "memorized" ? { background: "white" } : {}}
              data-testid="filter-memorized">
              Memorized
            </button>
            <button onClick={() => setFilterStatus("review")}
              className={`tt-btn px-2 py-2 text-xs flex-1 ${filterStatus === "review" ? "" : ""}`}
              style={filterStatus === "review"
                ? { background: "#7C3AED", color: "white" }
                : { background: "white" }}
              data-testid="filter-review">
              <RotateCcw className="w-3 h-3 inline mr-0.5" /> Review
            </button>
          </div>

          {/* Favorites View */}
          {filterStatus === "favorites" && (
            favorites.length === 0 ? (
              <div className="parchment rounded-xl p-8 text-center">
                <p className="font-heading text-lg" style={{ color: "#7A5946" }}>No favorites yet!</p>
                <p className="text-sm mt-2" style={{ color: "#7A5946" }}>Tap the heart on any item to add it to your favorites.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {favorites.map(item => {
                  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.other;
                  return (
                    <button key={item.id} onClick={() => { setSelectedItem(item); setView("item-detail"); }}
                      className="book-spine w-full text-left p-3 flex items-center gap-3"
                      style={{ background: config.bg }}
                      data-testid={`fav-item-${item.id}`}>
                      <Heart className="w-4 h-4 flex-shrink-0" fill="#D97706" style={{ color: "#D97706" }} />
                      <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ background: config.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-sm font-semibold truncate" style={{ color: "#4A3224" }}>{item.title}</p>
                        {item.author && <p className="text-xs" style={{ color: "#7A5946" }}>by {item.author}</p>}
                      </div>
                      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D8C3B5" }} />
                    </button>
                  );
                })}
              </div>
            )
          )}

          {/* Review View */}
          {filterStatus === "review" && (() => {
            const COMPANION_QUOTES = [
              { text: "A good poem becomes a lifelong companion to the mind, staying with you to provide comfort in old age.", author: "William Wordsworth" },
              { text: "A beautiful phrase is like a beautiful flower, and a beautiful quote is like a friend that stays with you forever.", author: "Hazrat Inayat Khan" },
              { text: "A poem is a circle of friendship. It is an intimate act between two strangers that lasts a lifetime.", author: "Edward Hirsch" },
              { text: "To pay attention, this is our endless and proper work... and a poem is a companion that walks beside you in that work.", author: "Mary Oliver" },
            ];
            const quote = COMPANION_QUOTES[Math.floor(new Date().getDate() % COMPANION_QUOTES.length)];

            if (reviewItem && reviewResult) {
              return (
                <div className="parchment rounded-xl p-6 text-center" data-testid="review-result">
                  <div className="text-4xl mb-3">
                    {reviewResult.new_tier >= reviewResult.old_tier ? "🌟" : "💛"}
                  </div>
                  <p className="font-heading text-lg font-bold mb-1" style={{ color: "#4A3224" }}>
                    {reviewResult.tier4_bonus ? "This one's truly yours now!" : reviewResult.new_tier > reviewResult.old_tier ? "Growing stronger!" : reviewResult.new_tier < reviewResult.old_tier ? "It'll come back to you." : "Holding steady — well done."}
                  </p>
                  <p className="text-sm mb-2" style={{ color: "#7A5946" }}>{reviewResult.tier_name}</p>
                  {reviewResult.coins_earned > 0 && (
                    <div className="flex items-center justify-center gap-1 mb-4">
                      <img src={GOLD_COIN_URL} alt="coin" className="w-5 h-5" />
                      <span className="text-sm font-bold" style={{ color: "#D97706" }}>+{reviewResult.coins_earned} coin{reviewResult.coins_earned !== 1 ? "s" : ""}</span>
                    </div>
                  )}
                  <button onClick={() => { setReviewItem(null); setReviewResult(null); }}
                    className="tt-btn tt-btn-ocean px-6 py-2" data-testid="review-done-btn">
                    Continue
                  </button>
                </div>
              );
            }

            if (reviewItem) {
              return (
                <div data-testid="review-practice">
                  <button onClick={() => { setReviewItem(null); setReviewResult(null); }}
                    className="flex items-center gap-2 mb-3 font-heading font-semibold text-sm"
                    style={{ color: "#7A5946" }}>
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <div className="parchment rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading text-base font-bold" style={{ color: "#4A3224" }}>{reviewItem.title}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                        style={{ background: {1:"#FEE2E2",2:"#FEF3C7",3:"#DBEAFE",4:"#FDE68A"}[reviewItem.review_tier||1],
                          color: {1:"#DC2626",2:"#D97706",3:"#2563EB",4:"#92400E"}[reviewItem.review_tier||1] }}>
                        Tier {reviewItem.review_tier || 1}
                      </span>
                    </div>
                    {reviewItem.author && <p className="text-xs mb-3" style={{ color: "#7A5946" }}>by {reviewItem.author}</p>}
                    <p className="text-xs mb-4" style={{ color: "#7A5946" }}>Take your time with this one. When you're ready, choose how to practice — then let us know how it went.</p>
                    <div className="space-y-2 mb-4">
                      <button onClick={() => { setSelectedItem(reviewItem); setPracticeMode("first_letters"); setView("practice"); }}
                        className="tt-btn tt-btn-ocean w-full py-2 text-sm" data-testid="review-first-letters">
                        Practice: First Letters
                      </button>
                      <button onClick={() => { setSelectedItem(reviewItem); setPracticeMode("fill_blanks"); setView("practice"); }}
                        className="tt-btn tt-btn-coral w-full py-2 text-sm" data-testid="review-fill-blanks">
                        Practice: Fill in the Blanks
                      </button>
                    </div>
                  </div>
                  <p className="font-heading text-sm font-semibold mb-3 text-center" style={{ color: "#4A3224" }}>How did it go?</p>
                  <div className="space-y-2">
                    <button onClick={() => submitReview(reviewItem.id, "nailed_it")}
                      className="tt-card w-full p-3 flex items-center gap-3 text-left hover:scale-[1.01] transition-transform"
                      data-testid="rate-nailed-it">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#DCFCE7" }}>
                        <Check className="w-5 h-5" style={{ color: "#16A34A" }} />
                      </div>
                      <div>
                        <p className="font-heading text-sm font-semibold" style={{ color: "#16A34A" }}>Nailed It</p>
                        <p className="text-[10px]" style={{ color: "#7A5946" }}>Recited it perfectly — move up a tier</p>
                      </div>
                    </button>
                    <button onClick={() => submitReview(reviewItem.id, "stumbled")}
                      className="tt-card w-full p-3 flex items-center gap-3 text-left hover:scale-[1.01] transition-transform"
                      data-testid="rate-stumbled">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#FEF3C7" }}>
                        <Star className="w-5 h-5" style={{ color: "#D97706" }} />
                      </div>
                      <div>
                        <p className="font-heading text-sm font-semibold" style={{ color: "#D97706" }}>Almost There</p>
                        <p className="text-[10px]" style={{ color: "#7A5946" }}>Hesitated a little — stay in this tier</p>
                      </div>
                    </button>
                    {(reviewItem.review_tier || 1) >= 2 && (
                      <button onClick={() => submitReview(reviewItem.id, "needed_help")}
                        className="tt-card w-full p-3 flex items-center gap-3 text-left hover:scale-[1.01] transition-transform"
                        data-testid="rate-needed-help">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#FEE2E2" }}>
                          <AlertTriangle className="w-5 h-5" style={{ color: "#DC2626" }} />
                        </div>
                        <div>
                          <p className="font-heading text-sm font-semibold" style={{ color: "#DC2626" }}>Needed a Nudge</p>
                          <p className="text-[10px]" style={{ color: "#7A5946" }}>Forgot some lines — move down a tier for more practice</p>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            // Review overview
            if (!reviewData) {
              return <div className="text-center py-8"><p className="text-sm" style={{ color: "#7A5946" }}>Loading...</p></div>;
            }

            if (reviewData.due.length === 0 && reviewData.upcoming.length === 0) {
              return (
                <div className="parchment rounded-xl p-8 text-center">
                  <p className="font-heading text-lg" style={{ color: "#7A5946" }}>No reviews yet</p>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "#7A5946" }}>Once you memorize items, they'll appear here on a gentle review schedule — like old friends waiting to visit.</p>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                {/* Companion Quote */}
                <div className="rounded-xl p-4" style={{ background: "#FFF7ED", border: "1px solid #FDE68A" }}>
                  <p className="text-xs italic leading-relaxed" style={{ color: "#7A5946" }}>"{quote.text}"</p>
                  <p className="text-[10px] mt-1 text-right" style={{ color: "#A0896C" }}>— {quote.author}</p>
                </div>

                {/* Ready to Revisit */}
                {reviewData.due.length > 0 && (
                  <div>
                    <p className="font-heading text-sm font-bold mb-2 flex items-center gap-1" style={{ color: "#7A5946" }}>
                      Ready to revisit ({reviewData.due.length})
                    </p>
                    <div className="space-y-2">
                      {reviewData.due.map(item => (
                        <button key={item.id} onClick={() => { setReviewItem(item); setReviewResult(null); }}
                          className="tt-card w-full p-3 flex items-center gap-3 text-left hover:scale-[1.01] transition-transform"
                          style={{ borderLeft: "3px solid #D97706" }}
                          data-testid={`review-due-${item.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-heading text-sm font-semibold truncate" style={{ color: "#4A3224" }}>{item.title}</p>
                            {item.author && <p className="text-[10px]" style={{ color: "#7A5946" }}>by {item.author}</p>}
                          </div>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0"
                            style={{ background: {1:"#FEE2E2",2:"#FEF3C7",3:"#DBEAFE",4:"#FDE68A"}[item.review_tier||1],
                              color: {1:"#DC2626",2:"#D97706",3:"#2563EB",4:"#92400E"}[item.review_tier||1] }}>
                            T{item.review_tier || 1}
                          </span>
                          <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D8C3B5" }} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Set */}
                {reviewData.due.length === 0 && (
                  <div className="parchment rounded-xl p-5 text-center mb-2">
                    <p className="font-heading text-base" style={{ color: "#16A34A" }}>You're all set for now</p>
                    <p className="text-xs mt-1" style={{ color: "#7A5946" }}>Enjoy your day — your poems will be here when you're ready.</p>
                  </div>
                )}

                {/* Coming Soon */}
                {reviewData.upcoming.length > 0 && (
                  <div>
                    <p className="font-heading text-sm font-bold mb-1" style={{ color: "#A0896C" }}>
                      Coming soon ({reviewData.upcoming.length})
                    </p>
                    <p className="text-[10px] mb-2" style={{ color: "#A0896C" }}>Scheduled for later, but feel free to revisit anytime.</p>
                    <div className="space-y-2">
                      {reviewData.upcoming.slice(0, 10).map(item => {
                        const dueDate = new Date(item.next_review_date);
                        const daysUntil = Math.max(0, Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)));
                        return (
                          <button key={item.id} onClick={() => { setReviewItem(item); setReviewResult(null); }}
                            className="tt-card w-full p-3 flex items-center gap-3 text-left hover:scale-[1.01] transition-transform"
                            data-testid={`review-upcoming-${item.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-heading text-sm font-semibold truncate" style={{ color: "#4A3224" }}>{item.title}</p>
                              <p className="text-[10px]" style={{ color: "#A0896C" }}>
                                scheduled in {daysUntil} day{daysUntil !== 1 ? "s" : ""}
                              </p>
                            </div>
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0"
                              style={{ background: {1:"#FEE2E2",2:"#FEF3C7",3:"#DBEAFE",4:"#FDE68A"}[item.review_tier||1],
                                color: {1:"#DC2626",2:"#D97706",3:"#2563EB",4:"#92400E"}[item.review_tier||1] }}>
                              T{item.review_tier || 1}
                            </span>
                            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D8C3B5" }} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tier Guide */}
                <div className="pt-2" style={{ borderTop: "1px dashed #D8C3B5" }}>
                  <p className="text-[10px] mb-1" style={{ color: "#A0896C" }}>Review Tiers:</p>
                  <div className="flex gap-2 flex-wrap">
                    {[{t:1,n:"Freshly Memorized",d:"every 3 days"},{t:2,n:"Stable",d:"every 10 days"},{t:3,n:"Deeply Ingrained",d:"every 30 days"},{t:4,n:"Permanent",d:"every 90 days"}].map(t => (
                      <span key={t.t} className="text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{ background: {1:"#FEE2E2",2:"#FEF3C7",3:"#DBEAFE",4:"#FDE68A"}[t.t],
                          color: {1:"#DC2626",2:"#D97706",3:"#2563EB",4:"#92400E"}[t.t] }}>
                        T{t.t}: {t.d}
                      </span>
                    ))}
                  </div>
                  <p className="text-[9px] mt-2" style={{ color: "#A0896C" }}>Every 5 reviews earns a gold coin. Reaching Tier 4 earns 3 bonus coins!</p>
                </div>
              </div>
            );
          })()}

          {/* Type Shelves (not shown on favorites or review tab) */}
          {filterStatus !== "favorites" && filterStatus !== "review" && (Object.keys(typeGroups).length === 0 ? (
            <div className="parchment rounded-xl p-8 text-center">
              <p className="font-heading text-lg" style={{ color: "#7A5946" }}>
                {filterStatus === "not_working_on" ? "No saved items yet!" : filterStatus === "working_on" ? "No items being worked on!" : "Nothing memorized yet!"}
              </p>
              <p className="text-sm mt-2" style={{ color: "#7A5946" }}>
                {filterStatus === "not_working_on" ? "Visit the Library to add items to your collection!" : filterStatus === "working_on" ? "Move items from Saved to start working on them!" : "Keep practicing to memorize items!"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(typeGroups).map(([type, typeItems]) => {
                const config = TYPE_CONFIG[type] || TYPE_CONFIG.other;
                return (
                  <button key={type} onClick={() => { setFilterType(type); setView("type-items"); }}
                    className="tt-card w-full p-4 flex items-center gap-4 text-left"
                    data-testid={`type-shelf-${type}`}>
                    <TypeIcon type={type} size={48} />
                    <div className="flex-1">
                      <p className="font-heading text-base font-semibold" style={{ color: "#4A3224" }}>{config.label}</p>
                      <p className="text-xs" style={{ color: "#7A5946" }}>{typeItems.length} items</p>
                    </div>
                    <ChevronRight className="w-5 h-5" style={{ color: "#D8C3B5" }} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ═══════ TYPE ITEMS VIEW ═══════
  if (view === "type-items") {
    const config = TYPE_CONFIG[filterType] || TYPE_CONFIG.other;
    return (
      <div className="slide-up" data-testid="type-items-view">
        <button onClick={() => setView("choose")} className="flex items-center gap-2 mb-4 font-heading font-semibold"
          style={{ color: "#7A5946" }} data-testid="back-to-collection">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="open-book p-6">
          <div className="flex items-center gap-3 mb-4">
            <TypeIcon type={filterType} size={48} />
            <h2 className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>{config.label}</h2>
          </div>
          <div className="space-y-2">
            {currentTypeItems.map(item => (
              <button key={item.id} onClick={() => { setSelectedItem(item); setView("item-detail"); }}
                className="book-spine w-full text-left p-3 flex items-center gap-3"
                style={{ background: config.bg }}
                data-testid={`collection-item-${item.id}`}>
                <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ background: config.color }} />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-semibold truncate" style={{ color: "#4A3224" }}>{item.title}</p>
                  {item.author && <p className="text-xs" style={{ color: "#7A5946" }}>by {item.author}</p>}
                </div>
                {item.is_favorite && <Heart className="w-4 h-4 flex-shrink-0" fill="#D97706" style={{ color: "#D97706" }} />}
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D8C3B5" }} />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ═══════ ITEM DETAIL VIEW ═══════
  if (view === "item-detail" && selectedItem) {
    return (
      <div className="slide-up" data-testid="item-detail-view">
        <button onClick={() => setView("type-items")} className="flex items-center gap-2 mb-4 font-heading font-semibold"
          style={{ color: "#7A5946" }} data-testid="back-to-type">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="open-book p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>{selectedItem.title}</h2>
            <button onClick={() => toggleFavorite(selectedItem)} data-testid="toggle-favorite">
              <Heart className="w-5 h-5" fill={selectedItem.is_favorite ? "#D97706" : "none"} style={{ color: "#D97706" }} />
            </button>
          </div>
          {selectedItem.author && <p className="text-sm mb-4" style={{ color: "#7A5946" }}>by {selectedItem.author}</p>}

          <div className="parchment rounded-xl p-5 mb-6">
            <pre className="whitespace-pre-wrap text-sm leading-7"
              style={{ fontFamily: "'Nunito', sans-serif", color: "#4A3224" }}
              data-testid="item-content">{selectedItem.content}</pre>
          </div>

          <div className="space-y-3">
            {/* Status move buttons */}
            {selectedItem.status === "not_working_on" && (
              <button onClick={() => changeStatus(selectedItem, "working_on")}
                className="tt-btn tt-btn-ocean w-full py-3 text-base"
                data-testid="move-to-working-on">
                Start Working On This
              </button>
            )}
            {selectedItem.status === "working_on" && (
              <>
                <button onClick={() => { setPracticeMode("first_letters"); setView("practice"); }}
                  className="tt-btn tt-btn-ocean w-full py-3 text-base"
                  data-testid="practice-first-letters">
                  Practice: First Letters Only
                </button>
                <button onClick={() => { setPracticeMode("fill_blanks"); setView("practice"); }}
                  className="tt-btn tt-btn-coral w-full py-3 text-base"
                  data-testid="practice-fill-blanks">
                  Practice: Fill in the Blanks
                </button>
                <button onClick={() => markMemorized(selectedItem)}
                  className="tt-btn tt-btn-gold w-full py-3 text-base"
                  data-testid="mark-memorized-btn">
                  I Got It! Mark as Memorized
                </button>
                <button onClick={() => changeStatus(selectedItem, "not_working_on")}
                  className="tt-btn w-full py-2 text-sm"
                  style={{ background: "white" }}
                  data-testid="move-to-saved">
                  Move Back to Saved
                </button>
              </>
            )}
            {selectedItem.status === "memorized" && (
              <>
                <button onClick={() => { setPracticeMode("first_letters"); setView("practice"); }}
                  className="tt-btn tt-btn-ocean w-full py-3 text-base"
                  data-testid="practice-first-letters">
                  Review: First Letters Only
                </button>
                <button onClick={() => { setPracticeMode("fill_blanks"); setView("practice"); }}
                  className="tt-btn tt-btn-coral w-full py-3 text-base"
                  data-testid="practice-fill-blanks">
                  Review: Fill in the Blanks
                </button>
              </>
            )}

            {/* Remove from collection - subtle */}
            <button onClick={() => removeFromCollection(selectedItem)}
              className="w-full text-center py-2 text-xs flex items-center justify-center gap-1 opacity-40 hover:opacity-80 transition-opacity mt-2"
              style={{ color: "#7A5946" }}
              data-testid="remove-from-collection-btn">
              <Trash2 className="w-3 h-3" /> Remove from collection
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════ PRACTICE VIEW ═══════
  if (view === "practice" && selectedItem) {
    return (
      <PracticeMode
        item={selectedItem}
        mode={practiceMode}
        onBack={() => setView("item-detail")}
        onMemorized={() => markMemorized(selectedItem)}
        userId={currentUser?.id}
      />
    );
  }

  return null;
}
